import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Navbar.css';
import CosmicLogo from '../common/CosmicLogo';
import { auth, db } from '../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useKundli } from '../../context/KundliContext';
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import UserProfileModal from '../Dashboard/UserProfileModal';
import { AuthModal } from '../../pages/LandingPage';

// ── Language Toggle ────────────────────────────────────────────────────────
const LangToggle = () => {
  const { lang, toggleLang } = useLanguage();
  const isHindi = lang === 'hi';
  return (
    <button
      className="nav-lang-toggle"
      onClick={toggleLang}
      title={isHindi ? 'Switch to English' : 'हिंदी में बदलें'}
      aria-label="Toggle language"
    >
      <span className={`nav-lang-opt ${!isHindi ? 'nav-lang-active' : ''}`}>EN</span>
      <span className="nav-lang-divider">|</span>
      <span className={`nav-lang-opt ${isHindi ? 'nav-lang-active' : ''}`}>हि</span>
    </button>
  );
};

const Navbar = () => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const { kundliData, kundliChartData, setKundliData, setKundliChartData } = useKundli();
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const navRef = useRef(null);

  // Always enforce dark mode
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
    localStorage.removeItem('theme');
  }, []);

  // Expose navbar height to CSS variables for perfectly sticky tabs
  useEffect(() => {
    const updateHeight = () => {
      if (navRef.current) {
        document.documentElement.style.setProperty('--navbar-height', `${navRef.current.offsetHeight}px`);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleNew = () => {
    localStorage.removeItem("kundliData");
    localStorage.removeItem("kundliChartData");
    setKundliData(null);
    setKundliChartData(null);
    navigate("/dashboard");
  };

  const handleOpen = () => navigate("/saved-charts");

  const handleSave = async () => {
    if (!kundliData || !kundliChartData) {
      alert("Generate a chart first before saving!");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      alert("You must be logged in to save charts.");
      return;
    }
    setSaving(true);
    try {
      const chartName = prompt("Enter a name for this chart:", kundliData.name || "My Cosmic Chart");
      if (!chartName) { setSaving(false); return; }
      await addDoc(collection(db, `users/${user.uid}/savedCharts`), {
        name: chartName, 
        kundliData, 
        createdAt: Date.now()
      });
      alert("Chart saved successfully!");
    } catch (error) {
      console.error("Error saving chart:", error);
      alert("Failed to save chart. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <nav className="dashboard-navbar" ref={navRef}>
      <div className="nav-left" style={{ display: 'flex', alignItems: 'center' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer', width: 'max-content' }}>
          <CosmicLogo size={48} style={{ marginRight: '14px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: '4px' }}>
            <h1 className="text-gradient-gold nav-brand-title" style={{ fontFamily: 'Antonio, sans-serif', fontSize: '1.8rem', fontWeight: '600', letterSpacing: '6px', margin: 0, lineHeight: '1', textTransform: 'uppercase' }}>COSPYRIA</h1>
            <span className="nav-brand-subtitle" style={{ fontSize: '0.6rem', color: '#fff', letterSpacing: '2px', fontWeight: '300', marginTop: '4px' }}>VEDIC ASTROLOGY ENGINE</span>
          </div>
        </Link>
      </div>

      <div className="nav-center">
        <button className="nav-action-btn" onClick={handleNew}>
          <span className="icon">✨</span> {t('New')}
        </button>
        <button className="nav-action-btn" onClick={handleOpen}>
          <span className="icon">📂</span> {t('Open')}
        </button>
        <button className="nav-action-btn" onClick={handleSave} disabled={saving}>
          <span className="icon">💾</span> {saving ? t('Saving...') : t('Save')}
        </button>
      </div>

      <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <LangToggle />
        {currentUser ? (
          <button className="nav-profile-btn" onClick={() => setShowProfileModal(true)}>
            <span className="icon">👤</span> {t('Profile')}
          </button>
        ) : (
          <>
            <button className="nav-action-btn" onClick={() => setAuthMode("login")}>{t('Sign In')}</button>
            <button className="nav-auth-btn" onClick={() => setAuthMode("signup")} style={{ background: '#D4AF37', color: '#000', padding: '0.4rem 1rem', borderRadius: '4px', fontWeight: 'bold' }}>{t('Get Started')}</button>
          </>
        )}
      </div>

      {showProfileModal && (
        <UserProfileModal onClose={() => setShowProfileModal(false)} />
      )}
      
      {authMode && (
        <AuthModal 
          mode={authMode} 
          onClose={() => setAuthMode(null)} 
          onToggleMode={() => setAuthMode(authMode === "signup" ? "login" : "signup")}
          onAuthSuccess={(isPrem) => {
            setAuthMode(null);
          }}
        />
      )}
    </nav>
  );
};

export default Navbar;
